import { useCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { PostAdd as PostAddIcon } from 'components/icons';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import ExpenseModal from 'components/modals/expenses/ExpenseModal.tsx';
import { SwitchMatterModalResult } from 'components/modals/invoices/SwitchMatterModal';
import { useModal } from 'components/modals/useModal.ts';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util.tsx';
import { useCurrentEmployee } from 'pages/employees/useCurrentEmployee.ts';
import ExpensesBulkSelectModal from 'pages/expenses/components/ExpensesBulkSelectModal.tsx';
import { useInvoice } from 'pages/invoices/hooks/useInvoice.ts';
import { DetailsTabSuccessMessages } from 'pages/invoices/InvoicePage.tsx';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import {
    Control,
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormReset,
    UseFormSetValue,
    useWatch,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { INVOICE_TAB_PATHS } from 'routes/constants';
import { useSelector } from 'store';
import {
    useAddInvoiceExpenseMutation,
    useCopyInvoiceExpenseMutation,
    useUpdateInvoiceExpensesMutation,
} from 'store/api/invoice-details';
import { ClassifierData } from 'types/classifiers';
import { ExpenseCreateDto, ExpenseForm } from 'types/expenses.ts';
import { FeatureFlag } from 'types/features';
import {
    InvoiceExpense,
    InvoiceExpenseBulkUpdateDto,
    InvoiceExpenseForm,
    InvoiceMatterForm,
    InvoiceMatterSummary,
} from 'types/invoice-details';
import { EditIntentTypeCode, InvoiceData, InvoiceStatusType } from 'types/invoices';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { createTabState } from 'utils/tabStateUtil';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import ExpenseActions from '../../components/ExpenseActions';
import InvoiceLoader from '../../components/InvoiceLoader';
import { ItemActionButton } from '../../components/ItemActionButton';
import { AdvancedBillingSwitch, ItemTableCard, SECTION_TITLE } from '../SectionCard';
import InvoiceExpenseTableItem from './InvoiceExpenseTableItem';
import InvoiceExpenseTableTotals from './InvoiceExpenseTableTotals';
import { DIALOG_560 } from 'components/modals/dialogWidths';

export interface InvoiceExpenseTableProps {
    addedInvoiceExpenseIds: number[];
    setAddedInvoiceExpenseIds: Dispatch<SetStateAction<number[]>>;
    successMessages: DetailsTabSuccessMessages;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
    isInViewMode: boolean;
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterSummary;
    isLoading: boolean;
    classifiers: Record<string, ClassifierData>;
    validateForm: UseFormHandleSubmit<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    reset: UseFormReset<InvoiceMatterForm>;
    control: Control<InvoiceMatterForm>;
}

type Props = InvoiceExpenseTableProps;

/** A case's expense list: its options, its add and its bulk actions, as both designs' lists use them. */
export const useInvoiceExpenseTable = ({
    invoice,
    invoiceMatter,
    getValues,
    setValue,
    setAddedInvoiceExpenseIds,
    setSuccessMessages,
}: Pick<
    Props,
    'invoice' | 'invoiceMatter' | 'getValues' | 'setValue' | 'setAddedInvoiceExpenseIds' | 'setSuccessMessages'
>) => {
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const { data: currentEmployee } = useCurrentEmployee();
    const isExpenseEntryDisabled = useExpenseEntryDisabled();
    const { openModal, closeModal } = useModal();
    const createDialog = useCreateDialog();
    const navigateWithReturn = useNavigateWithReturnState();
    const canCreateExpenses = useHasPermissions(['CREATE.EXPENSES']);
    const { id: invoiceId, hasCreditInvoice, isSplit, invoiceStatusTypeCode } = invoice;
    const { matterId, matterName, fullName: matterFullName } = invoiceMatter;
    const [advancedBillingEnabled, setAdvancedBillingEnabled] = useState(false);
    const [openModalForExpenseId, setOpenModalForExpenseId] = useState<number | null>(null);
    const { isDebit, isCorrection: isCorrectionInvoice } = useInvoice(invoice);
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);

    const clearModalRequest = useCallback(() => {
        setOpenModalForExpenseId(null);
    }, []);

    const isInProcessingState = [
        InvoiceStatusType.DRAFT,
        InvoiceStatusType.REVIEWED,
        InvoiceStatusType.APPROVED,
        InvoiceStatusType.FINALIZED,
    ]
        .map((status) => status.toString())
        .includes(invoiceStatusTypeCode);

    const allowsNewExpenses = useMemo(
        () => (isCorrectionInvoice || (isDebit && isInProcessingState)) && !hasCreditInvoice && !isSplit,
        [isInProcessingState, hasCreditInvoice, isSplit, isDebit, isCorrectionInvoice]
    );

    const [updateInvoiceExpenses] = useUpdateInvoiceExpensesMutation();
    const [copyInvoiceExpense] = useCopyInvoiceExpenseMutation();
    const [addInvoiceExpense] = useAddInvoiceExpenseMutation();

    const handleDuplicate = useCallback(() => {
        if (isExpenseEntryDisabled) {
            return;
        }
        const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');
        const selected = invoiceExpenses?.find((item) => item.selected);
        if (selected) {
            copyInvoiceExpense({ invoiceId, matterId, id: selected.id })
                .unwrap()
                .then((newExpense) => {
                    // Add the new expense ID to the highlighted list
                    setAddedInvoiceExpenseIds((prev) => [...prev, newExpense.expense.id ?? 0]);
                    setSuccessMessages((prev) => ({ ...prev, newExpensesAdded: true }));
                    // Set the modal to open for the new timecard
                    setOpenModalForExpenseId(newExpense.expense.id ?? 0);
                })
                .catch(console.error);
        }
    }, [
        getValues,
        copyInvoiceExpense,
        invoiceId,
        matterId,
        setAddedInvoiceExpenseIds,
        setSuccessMessages,
        isExpenseEntryDisabled,
    ]);

    const handleOnInvoiceExpenseCreate = useCallback(
        async (data: ExpenseCreateDto) => {
            try {
                addInvoiceExpense({
                    invoiceId: invoiceId,
                    matterId: matterId,
                    dto: data,
                })
                    .unwrap()
                    .then((newExpense: InvoiceExpense) => {
                        // Add the new expense ID to the highlighted list
                        setAddedInvoiceExpenseIds((prev) => [...prev, newExpense.expense.id ?? 0]);
                        setSuccessMessages((prev) => ({ ...prev, newExpensesAdded: true }));
                    });
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addInvoiceExpense, invoiceId, matterId, setAddedInvoiceExpenseIds, setSuccessMessages]
    );

    const openExpenseModal = useCallback((): void => {
        // Where the create dialog exists, Add expense creates through it, as Add timecard does, and lands back on
        // the Expenses tab.
        if (createDialog) {
            navigateWithReturn(`/invoice/${invoiceId}/matter/${matterId}/expenses/new`, {
                state: {
                    returnTo: INVOICE_TAB_PATHS.EXPENSES.replace(':id', String(invoiceId)),
                    ...createTabState('invoices-center-block', 'expenses'),
                },
            });
            return;
        }
        const initalData: ExpenseForm = {
            externalId: null,
            expenseType: null,
            currencyType: null,
            description: '',
            expenseAmount: '',
            expenseDate: formatDateStringInput(new Date()),
            isNonBillable: false,
            employee: currentEmployee
                ? { id: currentEmployee.id, name: `${currentEmployee.firstName} ${currentEmployee.lastName}` }
                : null,
            matter: { id: matterId, name: matterName, fullName: matterFullName, isNonBillable: false },
            effortId: null,
            timecardId: null,
        };

        openModal(
            <ExpenseModal
                onChange={(value) => {
                    void handleOnInvoiceExpenseCreate(value);
                }}
                initialData={initalData}
                onClose={closeModal}
                titleId={'expenses.add'}
                readOnly={false}
                isNewInvoiceExpense
                disableSelectMatter
                preselectMatterId={matterId}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    }, [
        createDialog,
        navigateWithReturn,
        invoiceId,
        currentEmployee,
        matterId,
        matterName,
        matterFullName,
        openModal,
        closeModal,
        handleOnInvoiceExpenseCreate,
    ]);

    const handleUpdateInvoiceExpenses = useCallback(
        async (idList: number[]) => {
            const dtoList: InvoiceExpenseBulkUpdateDto[] = [];
            const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');
            invoiceExpenses
                .filter((ex) => idList.includes(ex.id))
                .forEach((ex) =>
                    dtoList.push({
                        id: ex.id,
                        newMatterId: ex.newMatter?.id ?? null,
                        editIntentTypeCode: ex.editIntentTypeCode,
                        isNonBillable: ex.isNonBillable,
                    })
                );

            await updateInvoiceExpenses({ invoiceId, matterId, dtoList }).unwrap().catch(console.error);
        },
        [getValues, invoiceId, matterId, updateInvoiceExpenses]
    );

    const invoiceExpenseCount = invoiceMatter.invoiceExpenses.length;

    const handlePostpone = () => {
        const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');
        const idList: number[] = [];
        const updatedExpenses = invoiceExpenses.map((invoiceExpense) => {
            if (invoiceExpense.id && invoiceExpense.selected) {
                idList.push(invoiceExpense.id);
                return { ...invoiceExpense, selected: false, editIntentTypeCode: EditIntentTypeCode.POSTPONE };
            }
            return invoiceExpense;
        });
        setValue('invoiceExpenses', updatedExpenses, { shouldDirty: true });
        handleUpdateInvoiceExpenses(idList);
    };

    const handleRemove = () => {
        const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');
        const idList: number[] = [];
        const updatedExpenses = invoiceExpenses.map((invoiceExpense) => {
            if (invoiceExpense.id && invoiceExpense.selected) {
                idList.push(invoiceExpense.id);
                return {
                    ...invoiceExpense,
                    selected: false,
                    editIntentTypeCode: EditIntentTypeCode.REMOVE_VIA_CORRECTION,
                };
            }
            return invoiceExpense;
        });
        setValue('invoiceExpenses', updatedExpenses, { shouldDirty: true });
        handleUpdateInvoiceExpenses(idList);
    };

    const onBulkSelect = useCallback(
        (isToSelect: boolean, expenseIds: number[]) => {
            const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');

            const updatedInvoiceExpenses = invoiceExpenses.map((item) => {
                if (item.id && expenseIds.includes(item.id)) {
                    return { ...item, selected: isToSelect };
                }
                return item;
            });
            setValue('invoiceExpenses', updatedInvoiceExpenses, { shouldDirty: true, shouldValidate: false });
        },
        [getValues, setValue]
    );

    const handleBulkSelect = () => {
        openModal(
            <ExpensesBulkSelectModal
                onBulkSelect={(ids) => onBulkSelect(true, ids)}
                onBulkUnselect={(ids) => onBulkSelect(false, ids)}
                onClose={closeModal}
                expenses={invoiceMatter.invoiceExpenses.map((exp) => ({
                    ...exp,
                    isSelectable: exp.editIntentTypeCode == null,
                }))}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE,
            undefined,
            DIALOG_560
        );
    };

    const handleNonBillable = () => {
        const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');
        const idList: number[] = [];
        const updatedExpenses = invoiceExpenses.map((invoiceExpense) => {
            if (invoiceExpense.id && invoiceExpense.selected) {
                idList.push(invoiceExpense.id);
                return {
                    ...invoiceExpense,
                    selected: false,
                    isNonBillable: true,
                    editIntentTypeCode: EditIntentTypeCode.MARK_AS_NONBILLABLE,
                };
            }
            return invoiceExpense;
        });
        setValue('invoiceExpenses', updatedExpenses, { shouldDirty: true });
        handleUpdateInvoiceExpenses(idList);
    };

    const handleMove = (value: SwitchMatterModalResult) => {
        const invoiceExpenses: InvoiceExpenseForm[] = getValues('invoiceExpenses');
        const idList: number[] = [];
        const newMatter =
            value.matter && value.matter.id !== invoiceMatter.matterId
                ? { id: value.matter.id, name: value.matter.fullName }
                : null;
        const updatedExpenses = invoiceExpenses.map((invoiceExpense) => {
            if (invoiceExpense.id && invoiceExpense.selected) {
                idList.push(invoiceExpense.id);
                return {
                    ...invoiceExpense,
                    selected: false,
                    newMatter,
                    editIntentTypeCode: newMatter ? EditIntentTypeCode.MOVE_TO_ANOTHER_CASE : null,
                };
            }
            return invoiceExpense;
        });
        setValue('invoiceExpenses', updatedExpenses, { shouldDirty: true });
        handleUpdateInvoiceExpenses(idList);
    };

    return {
        buttonsDisabled,
        isExpenseEntryDisabled,
        canCreateExpenses,
        invoiceId,
        matterId,
        advancedBillingEnabled,
        setAdvancedBillingEnabled,
        openModalForExpenseId,
        clearModalRequest,
        isCorrectionInvoice,
        isPaymentCurrencyMode,
        displayCurrency,
        allowsNewExpenses,
        handleDuplicate,
        openExpenseModal,
        handleUpdateInvoiceExpenses,
        invoiceExpenseCount,
        handlePostpone,
        handleRemove,
        onBulkSelect,
        handleBulkSelect,
        handleNonBillable,
        handleMove,
    };
};

const InvoiceExpenseTable = ({
    classifiers,
    addedInvoiceExpenseIds,
    setAddedInvoiceExpenseIds,
    setSuccessMessages,
    isInViewMode,
    invoiceMatter,
    invoice,
    isLoading,
    validateForm,
    setValue,
    getValues,
    reset,
    control,
}: Props) => {
    const {
        buttonsDisabled,
        isExpenseEntryDisabled,
        canCreateExpenses,
        invoiceId,
        matterId,
        advancedBillingEnabled,
        setAdvancedBillingEnabled,
        openModalForExpenseId,
        clearModalRequest,
        isCorrectionInvoice,
        isPaymentCurrencyMode,
        displayCurrency,
        allowsNewExpenses,
        handleDuplicate,
        openExpenseModal,
        handleUpdateInvoiceExpenses,
        invoiceExpenseCount,
        handlePostpone,
        handleRemove,
        handleBulkSelect,
        handleNonBillable,
        handleMove,
    } = useInvoiceExpenseTable({
        invoice,
        invoiceMatter,
        getValues,
        setValue,
        setAddedInvoiceExpenseIds,
        setSuccessMessages,
    });

    const addExpenseButton = (
        <div className="m-2 inline-flex">
            <ItemActionButton
                tooltip={
                    isExpenseEntryDisabled
                        ? t('expenses.integration-entry-disabled')
                        : t('invoices.tooltips.add-new-expense')
                }
                disabled={isExpenseEntryDisabled || isInViewMode || !canCreateExpenses || buttonsDisabled}
                onClick={openExpenseModal}
            >
                <PostAddIcon />
            </ItemActionButton>
        </div>
    );

    const watchedExpenses = useWatch({ control, name: 'invoiceExpenses' });

    const tableContent = invoiceMatter.invoiceExpenses.map((field, index) => (
        <InvoiceExpenseTableItem
            key={field.id}
            readonly={isInViewMode || isExpenseEntryDisabled}
            index={index}
            invoiceExpense={field}
            expenseForm={watchedExpenses?.[index]}
            invoice={invoice}
            classifiers={classifiers}
            isNew={field.expense !== null ? addedInvoiceExpenseIds.includes(field.expense.id ?? 0) : false}
            control={control}
            validateForm={validateForm}
            setValue={setValue}
            getValues={getValues}
            reset={reset}
            handleUpdateInvoiceExpenses={(idList) => {
                void handleUpdateInvoiceExpenses(idList);
            }}
            advancedBillingEnabled={advancedBillingEnabled}
            shouldOpenModal={field.expense?.id === openModalForExpenseId}
            clearModalRequest={clearModalRequest}
        />
    ));

    if (isLoading) {
        return <InvoiceLoader />;
    }

    if (invoiceExpenseCount === 0 && allowsNewExpenses) {
        return <div className="flex flex-col items-start">{addExpenseButton}</div>;
    }

    return (
        <ItemTableCard
            title={
                <p className={cn(SECTION_TITLE, 'whitespace-nowrap')}>
                    <FormattedMessage id="invoices.details.expenses" values={{ count: invoiceExpenseCount }} />
                </p>
            }
            actions={
                <>
                    <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_EXPENSE_EXTRA_OPTIONS}>
                        <AdvancedBillingSwitch
                            checked={advancedBillingEnabled}
                            onCheckedChange={setAdvancedBillingEnabled}
                            disabled={isInViewMode}
                        />
                    </FeatureFlagged>
                    {!isInViewMode || isCorrectionInvoice ? (
                        <ExpenseActions
                            handleMoveExpense={handleMove}
                            handleNonBillable={handleNonBillable}
                            handlePostpone={handlePostpone}
                            handleDuplicate={handleDuplicate}
                            handleRemove={handleRemove}
                            handleBulkSelect={handleBulkSelect}
                            control={control}
                            getValues={getValues}
                            invoiceId={invoiceId}
                            matterId={matterId}
                            invoiceAllowsNewExpenses={allowsNewExpenses}
                            userCanCreateExpenses={canCreateExpenses}
                            isCorrectionInvoice={isCorrectionInvoice}
                        />
                    ) : null}
                </>
            }
        >
            <InvoiceExpenseTableTotals
                invoiceExpenses={invoiceMatter.invoiceExpenses}
                currency={displayCurrency}
                isPaymentCurrencyMode={isPaymentCurrencyMode}
            />
            {/* The add button, when shown, spaces the list from the card's edge; otherwise the padding does. */}
            <div className={cn('flex flex-col', !allowsNewExpenses && 'pb-5')}>{tableContent}</div>
            {allowsNewExpenses && addExpenseButton}
        </ItemTableCard>
    );
};

export default InvoiceExpenseTable;
